import React from 'react';
import { Link } from 'react-router-dom';
import { Tag, FileText, Shield } from 'lucide-react';
import { transformEntriesToMap } from '../../utils/categoryEntries';
import { CustomerCategory } from '../../types/customerCategories';

interface APArchetype {
  id: string;
  name: string;
  description?: string;
  is_default?: boolean;
  descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
}

interface GovernanceOverlay {
  id: string;
  name: string;
  description?: string;
  is_default?: boolean;
  descriptions?: Array<{ key: string; value: any; is_active?: boolean }>;
}

type DataCardData = CustomerCategory | APArchetype | GovernanceOverlay;

interface DataCardProps {
  data: DataCardData;
  dataType: 'category' | 'apArchetype' | 'governanceOverlay';
  showLink?: boolean;
  compact?: boolean;
  linkPath?: string;
}

const DataCard: React.FC<DataCardProps> = ({ 
  data, 
  dataType,
  showLink = true, 
  compact = false,
  linkPath
}) => {
  console.log('DataCard received data:', data);
  console.log('Data type:', dataType);
  
  // Get the appropriate icon based on data type
  const renderFixedIcon = () => {
    switch (dataType) {
      case 'category':
        return <Tag className="w-6 h-6 text-slate-700" />;
      case 'apArchetype':
        return <FileText className="w-6 h-6 text-slate-700" />;
      case 'governanceOverlay':
        return <Shield className="w-6 h-6 text-slate-700" />;
      default:
        return <Tag className="w-6 h-6 text-slate-700" />;
    }
  };

  const getBadgeColor = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('key') || lowerName.includes('premium') || lowerName.includes('priority')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (lowerName.includes('regular') || lowerName.includes('standard') || lowerName.includes('normal')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (lowerName.includes('risky') || lowerName.includes('risk') || lowerName.includes('high-risk')) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (lowerName.includes('new') || lowerName.includes('pending') || lowerName.includes('review')) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (lowerName.includes('payment') || lowerName.includes('terms') || lowerName.includes('agreement')) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    } else if (lowerName.includes('monitoring') || lowerName.includes('enhanced') || lowerName.includes('surveillance')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    } else if (lowerName.includes('relationship') || lowerName.includes('protection') || lowerName.includes('strategic')) {
      return 'bg-green-100 text-green-800 border-green-200';
    } else if (lowerName.includes('legal') || lowerName.includes('hold') || lowerName.includes('action')) {
      return 'bg-red-100 text-red-800 border-red-200';
    } else if (lowerName.includes('cultural') || lowerName.includes('accommodation') || lowerName.includes('sensitivity')) {
      return 'bg-purple-100 text-purple-800 border-purple-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const renderValue = (key: string, value: any) => {
    if (Array.isArray(value)) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
          <ul className="space-y-1">
            {value.map((item, index) => (
              <li key={index} className="flex items-start">
                <span className="text-gray-500 text-sm mr-2">•</span>
                <span className="text-sm text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    } else if (typeof value === 'object' && value !== null) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
          <div className="overflow-hidden">
            <table className="min-w-full">
              <tbody>
                {Object.entries(value).map(([subKey, subValue]) => (
                  <tr key={subKey} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-2 pr-4 text-sm text-gray-600 font-medium align-top w-1/3">
                      {subKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:
                    </td>
                    <td className="py-2 text-sm text-gray-700 align-top">
                      {subValue as string}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-1">
          <h4 className="font-medium text-gray-900 text-sm">
            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </h4>
          <div className="text-sm text-gray-700">
            {value}
          </div>
        </div>
      );
    }
  };

  // Process data based on type
  let entries: any = [];
  let map: any = {};
  let otherFields: [string, any][] = [];
  let orderToEvaluate: any = null;

  if (dataType === 'category') {
    const category = data as CustomerCategory;
    entries = category.entries || category.category_entries;
    
    // If category_entries is an object, convert it to the expected format
    if (category.category_entries && !Array.isArray(category.category_entries)) {
      entries = Object.entries(category.category_entries).map(([key, value]) => ({
        id: key,
        key: key,
        value: typeof value === 'string' ? value : JSON.stringify(value)
      }));
    }
    
    // Ensure entries is an array before passing to transformEntriesToMap
    const entriesArray = Array.isArray(entries) ? entries : [];
    map = transformEntriesToMap(entriesArray);
    otherFields = Object.entries(map).filter(
      ([key]) => !['order_to_evaluate'].includes(key)
    );
    orderToEvaluate = map.order_to_evaluate;
  } else {
    // For AP Archetypes and Governance Overlays
    const typedData = data as APArchetype | GovernanceOverlay;
    const descriptions = typedData.descriptions || [];
    
    // Convert descriptions to the format expected by the card
    map = {};
    descriptions.forEach(desc => {
      if (!desc.hasOwnProperty('is_active') || desc.is_active !== false) {
        let value = desc.value;
        // Parse JSON strings
        if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
          try {
            value = JSON.parse(value);
          } catch {
            // Keep as string if parsing fails
          }
        }
        map[desc.key] = value;
      }
    });
    
    otherFields = Object.entries(map).filter(
      ([key]) => !['order_to_evaluate'].includes(key)
    );
    orderToEvaluate = map.order_to_evaluate;
  }

  console.log('Processed map:', map);
  console.log('Other fields:', otherFields);

  // If no entries are found, show a message
  if (otherFields.length === 0) {
    return (
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
        <div className="flex items-center space-x-3 mb-3">
          {renderFixedIcon()}
          <div className="flex-1">
            <h6 className="text-lg font-semibold text-gray-900">
              {data.name}
            </h6>
            {data.is_default && (
              <span className="text-xs text-green-600 font-medium">DEFAULT</span>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-3">{data.description}</p>
        <div className="text-sm text-gray-500 italic">
          No detailed configuration available for this {dataType === 'category' ? 'category' : dataType === 'apArchetype' ? 'archetype' : 'overlay'}.
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="bg-slate-50 rounded-lg border border-slate-200 p-4">
        <div className="flex items-center space-x-3 mb-3">
          {renderFixedIcon()}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              {showLink && linkPath ? (
                <Link
                  to={linkPath}
                  className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  {data.name}
                </Link>
              ) : (
                <h6 className="text-lg font-semibold text-gray-900">
                  {data.name}
                </h6>
              )}
              {data.is_default && (
                <span className="text-xs text-green-600 font-medium">DEFAULT</span>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-1">{data.description}</p>
          </div>
        </div>
        <div className="space-y-3">
          {otherFields.slice(0, 2).map(([key, value]) => (
            <div key={key}>
              {renderValue(key, value)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
      {/* Card Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="space-y-3">
          {/* Top row: Icon, Name, Evaluation Order */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {renderFixedIcon()}
              <div>
                {showLink && linkPath ? (
                  <Link
                    to={linkPath}
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    {data.name}
                  </Link>
                ) : (
                  <h6 className="text-lg font-semibold text-gray-900">
                    {data.name}
                  </h6>
                )}
                {data.is_default && (
                  <p className="text-xs text-green-600 font-medium mt-0.5">DEFAULT</p>
                )}
              </div>
            </div>
            {orderToEvaluate && (
              <div className="flex items-center">
                <span className={`px-4 py-2 rounded-full text-sm font-medium border whitespace-nowrap ${getBadgeColor(data.name)}`}>
                  Evaluation Order: {orderToEvaluate}
                </span>
              </div>
            )}
          </div>
          {/* Bottom row: Description spanning full width */}
          {data.description && (
            <div>
              <p className="text-sm text-slate-600">{data.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Card Content - Always Expanded */}
      <div className="p-4 space-y-4">
        {otherFields.map(([key, value]) => (
          <div key={key} className="border-b border-slate-100 pb-3 last:border-b-0 last:pb-0">
            {renderValue(key, value)}
          </div>
        ))}
      </div>
    </div>
  );
};

// Backward compatibility
const CategoryCard: React.FC<{ category: CustomerCategory; showLink?: boolean; compact?: boolean }> = ({ category, showLink = true, compact = false }) => {
  return <DataCard data={category} dataType="category" showLink={showLink} compact={compact} linkPath={`/customer-categories/${category.id}`} />;
};

export default DataCard;
export { CategoryCard };
